import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Work as WorkIcon,
  ManageAccounts as ManageAccountsIcon,
  VpnKey as VpnKeyIcon,
  Functions as FunctionsIcon,
  History as HistoryIcon,
  Delete as TrashIcon,
  Terminal as TerminalIcon,
  HelpOutline as HelpOutlineIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Timeline as TimelineIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Insights as InsightsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Shield as ShieldIcon,
  PeopleAlt as PeopleAltIcon,
  Apartment as ApartmentIcon,
  ReportProblem as ReportProblemIcon,
  AttachMoney as AttachMoneyIcon,
  Subscriptions as SubscriptionsIcon,
  Lan as LanIcon,
} from '@mui/icons-material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate, useLocation } from 'react-router-dom';
import SwaggerIcon from './icons/SwaggerIcon';
import EnvironmentIcon from './icons/EnvironmentIcon';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { isSuperAdminUser } from '../utils/superAdmin';
import { canAccessManageAccount } from '../utils/permissions';
import { useSubscriptionGuard } from '../context/SubscriptionGuardContext';
import { useProxyConnection } from '../context/ProxyConnectionContext';
import {
  DFT_BRAND,
  HEADER_HEIGHT_PX,
  SIDEBAR_COLLAPSED_WIDTH_PX,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_MOBILE_WIDTH,
  APP_PAGE_ZOOM_VAR,
  getSidebarWidthPx,
  sidebarPaperHeightForZoomSx,
} from '../styles/brandPalette';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onToggleOpen?: () => void;
  /** Toggle the bottom console panel instead of navigating to a route. */
  onConsoleToggle?: () => void;
  /** Controls selected state for the Console item when using split view. */
  isConsoleOpen?: boolean;
  /** Super-admin shell: only activity / audit navigation. */
  superAdminMode?: boolean;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export { SIDEBAR_COLLAPSED_WIDTH_PX, getSidebarWidthPx };

/** Above MUI Menu/Select modal backdrops so sidebar stays clickable while dropdowns are open. */
const sidebarStackingZIndex = (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 1;

const SIDEBAR_ICON_SIZE = { xs: 20, md: 21, lg: 22 } as const;
const SIDEBAR_ICON_SIZE_COLLAPSED = { xs: 26, md: 26, lg: 27, xl: 28 } as const;

type NavGroup = 'WORKSPACE' | 'BUILD & TEST' | 'TOOLS';

type NavItem = {
  text: string;
  icon: React.ReactNode;
  path: string;
  group?: NavGroup;
};

const baseMenuItems: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', group: 'WORKSPACE' },
  { text: 'AI Agent', icon: <SmartToyIcon />, path: '/ai-agent', group: 'WORKSPACE' },
  { text: 'Projects', icon: <WorkIcon />, path: '/projects', group: 'WORKSPACE' },
  { text: 'Environments', icon: <EnvironmentIcon />, path: '/environments', group: 'WORKSPACE' },
  { text: 'Swagger', icon: <SwaggerIcon />, path: '/swagger', group: 'BUILD & TEST' },
  { text: 'Collections', icon: <FolderIcon />, path: '/collections', group: 'BUILD & TEST' },
  { text: 'Test Plan', icon: <AutorenewIcon />, path: '/test-plans', group: 'BUILD & TEST' },
  { text: '.Env', icon: <VpnKeyIcon />, path: '/env-variables', group: 'BUILD & TEST' },
  { text: 'Functions', icon: <FunctionsIcon />, path: '/functions', group: 'BUILD & TEST' },
  { text: 'Proxy Settings', icon: <LanIcon />, path: '/proxy-settings', group: 'TOOLS' },
  { text: 'Console', icon: <TerminalIcon />, path: '/console', group: 'TOOLS' },
  { text: 'Help', icon: <HelpOutlineIcon />, path: '/help', group: 'TOOLS' },
  { text: 'Admin', icon: <AdminPanelSettingsIcon />, path: '/super-admin/dashboard', group: 'TOOLS' },
  { text: 'Login History', icon: <AdminPanelSettingsIcon />, path: '/super-admin/login-history', group: 'TOOLS' },
  { text: 'Activity', icon: <AdminPanelSettingsIcon />, path: '/super-admin/activity-logs', group: 'TOOLS' },
  { text: 'User Stats', icon: <AdminPanelSettingsIcon />, path: '/super-admin/user-details', group: 'TOOLS' },
];

const superAdminOnlyMenuItems: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/super-admin/dashboard' },
  { text: 'Activity', icon: <TimelineIcon />, path: '/super-admin/activity-logs' },
  { text: 'Environment Management', icon: <EnvironmentIcon />, path: '/super-admin/environment-management' },
  { text: 'Login History', icon: <HistoryIcon />, path: '/super-admin/login-history' },
  { text: 'User Management', icon: <PeopleAltIcon />, path: '/super-admin/user-management' },
  { text: 'Tenants', icon: <ApartmentIcon />, path: '/super-admin/tenant-management' },
  { text: 'Plan Management', icon: <SubscriptionsIcon />, path: '/super-admin/plan-management' },
  { text: 'Grievance', icon: <ReportProblemIcon />, path: '/super-admin/grievance' },
  { text: 'Revenue', icon: <AttachMoneyIcon />, path: '/super-admin/revenue' },
  { text: 'RBAC', icon: <ShieldIcon />, path: '/super-admin/rbac' },
  { text: 'Alerts', icon: <NotificationsActiveIcon />, path: '/super-admin/alerts' },
  { text: 'Advanced Monitoring', icon: <InsightsIcon />, path: '/super-admin/advanced-monitoring' },
  { text: 'System Configuration', icon: <SettingsIcon />, path: '/super-admin/system-configuration' },
  { text: 'Data & Reports', icon: <AssessmentIcon />, path: '/super-admin/data-reports' },
  { text: 'Security Controls', icon: <SecurityIcon />, path: '/super-admin/security-controls' },
];

const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose: _onClose,
  onToggleOpen,
  onConsoleToggle,
  isConsoleOpen,
  superAdminMode = false,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { handleProtectedNavigation } = useSubscriptionGuard();
  const { handleProxyProtectedNavigation } = useProxyConnection();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  const expanded = isMobile || open;

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (authLoading) return;
      if (!isAuthenticated) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      try {
        const response = await apiService.getTeamMembers();
        if (response.members !== undefined || !response.error) {
          setIsAdmin(true);
        }
      } catch {
        setIsAdmin((prev) => prev);
      } finally {
        setCheckingAdmin(false);
      }
    };

    checkAdminAccess();
  }, [isAuthenticated, authLoading]);

  const dismissOpenPageMenus = () => {
    document
      .querySelectorAll('[role="combobox"][aria-expanded="true"], .MuiSelect-select[aria-expanded="true"]')
      .forEach((el) => {
        (el as HTMLElement).blur();
      });
    window.dispatchEvent(new CustomEvent('app:close-profile-menu'));
  };

  const handleNavigation = (path: string) => {
    dismissOpenPageMenus();
    const runNavigation = () => {
      if (path === '/console') {
        onConsoleToggle?.();
        if (isMobile) onMobileClose?.();
        return;
      }
      navigate(path);
      if (isMobile) onMobileClose?.();
    };
    handleProtectedNavigation(path, () => {
      handleProxyProtectedNavigation(path, runNavigation);
    });
  };

  const isSuperAdmin = superAdminMode || isSuperAdminUser(user);
  const isAdminOnlyRole = user?.role === 'ADMIN' && !isSuperAdmin;
  const manageAccountItem: NavItem = {
    text: 'Manage Account',
    icon: <ManageAccountsIcon />,
    path: '/manage-account',
    group: 'TOOLS',
  };
  const showManageAccount =
    user?.role !== 'MEMBER' &&
    (canAccessManageAccount(user?.role) || (isAdmin && !checkingAdmin) || isAdminOnlyRole) &&
    !isSuperAdmin;

  const menuItems = isSuperAdmin
    ? superAdminOnlyMenuItems
    : isAdminOnlyRole
      ? [manageAccountItem]
      : [
          ...baseMenuItems.filter(
            (item) =>
              item.path !== '/super-admin/dashboard' &&
              item.path !== '/super-admin/login-history' &&
              item.path !== '/super-admin/activity-logs' &&
              item.path !== '/super-admin/user-details'
          ),
          ...(showManageAccount ? [manageAccountItem] : []),
          { text: 'History', icon: <HistoryIcon />, path: '/history', group: 'TOOLS' as const },
        ];

  const trashItem: NavItem = { text: 'Recycle Bin', icon: <TrashIcon />, path: '/trash' };

  const itemSelected = (path: string) =>
    path === '/console' ? !!isConsoleOpen : location.pathname === path;

  const listItemButtonSx = (path: string) => {
    const sel = itemSelected(path);
    return {
      minHeight: expanded
        ? { xs: 40, md: 42, lg: 44, xl: 46 }
        : { xs: 48, md: 48, lg: 48, xl: 50 },
      cursor: 'pointer',
      flexDirection: 'row' as const,
      justifyContent: expanded ? 'flex-start' : 'center',
      alignItems: 'center',
      px: expanded ? 1.25 : 0,
      py: { xs: 1, md: 1.05, lg: 1.15, xl: 1.25 },
      gap: expanded ? 1.25 : 0,
      borderRadius: '7px',
      background: sel ? DFT_BRAND.navSelected : 'transparent',
      boxShadow: sel ? DFT_BRAND.navSelectedShadow : 'none',
      color: sel ? '#ffffff' : DFT_BRAND.navText,
      '&.Mui-selected': {
        background: DFT_BRAND.navSelected,
        color: '#ffffff',
        '&:hover': {
          background: DFT_BRAND.navSelected,
        },
      },
      '&:hover': {
        background: sel ? DFT_BRAND.navSelected : DFT_BRAND.navHover,
        color: '#ffffff',
      },
      '&:focus-visible': {
        outline: `3px solid ${DFT_BRAND.focus}`,
        outlineOffset: -3,
      },
      transition: 'background 180ms ease, box-shadow 180ms ease, color 180ms ease',
    };
  };

  const iconSx = (path: string) => ({
    minWidth: expanded ? { xs: 24, md: 26, lg: 28 } : 0,
    mr: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: expanded ? SIDEBAR_ICON_SIZE : SIDEBAR_ICON_SIZE_COLLAPSED,
      height: expanded ? SIDEBAR_ICON_SIZE : SIDEBAR_ICON_SIZE_COLLAPSED,
      fontSize: expanded ? SIDEBAR_ICON_SIZE : SIDEBAR_ICON_SIZE_COLLAPSED,
      flexShrink: 0,
      display: 'block',
    },
    color: itemSelected(path) ? '#ffffff' : DFT_BRAND.navText,
  });

  const textSx = (path: string) => ({
    m: 0,
    minWidth: 0,
    '& .MuiListItemText-primary': {
      fontSize: { xs: '0.875rem !important', xl: '0.9375rem !important' },
      fontWeight: itemSelected(path) ? 600 : 500,
      color: itemSelected(path) ? '#ffffff' : DFT_BRAND.navText,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  });

  const drawerWidth = isMobile
    ? SIDEBAR_MOBILE_WIDTH
    : expanded
      ? SIDEBAR_EXPANDED_WIDTH
      : SIDEBAR_COLLAPSED_WIDTH_PX;
  const layoutWidth = isMobile ? 0 : 'var(--app-sidebar-width)';

  const renderNavButton = (item: NavItem) => {
    const button = (
      <ListItemButton
        selected={itemSelected(item.path)}
        onClick={() => handleNavigation(item.path)}
        sx={listItemButtonSx(item.path)}
        aria-label={item.text}
        aria-current={itemSelected(item.path) ? 'page' : undefined}
      >
        <ListItemIcon sx={iconSx(item.path)}>{item.icon}</ListItemIcon>
        {expanded && <ListItemText primary={item.text} sx={textSx(item.path)} />}
      </ListItemButton>
    );
    if (expanded) return button;
    return (
      <Tooltip title={item.text} placement="right" arrow>
        {button}
      </Tooltip>
    );
  };

  let lastGroup: string | undefined;

  const brandMark = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: expanded ? 'flex-start' : 'center',
        gap: 1.25,
        color: '#fff',
        mb: 3,
        px: 0,
        cursor: 'pointer',
        minHeight: expanded ? HEADER_HEIGHT_PX - 18 : 52,
      }}
      onClick={() => handleNavigation('/')}
    >
      <Box
        component="img"
        src="/dragonfly-mark.jpg"
        alt="CRUD Expert"
        sx={{
          width: expanded ? 36 : 40,
          height: expanded ? 43 : 48,
          objectFit: 'contain',
          borderRadius: '8px',
          flexShrink: 0,
          display: 'block',
        }}
      />
      {expanded && (
        <Box sx={{ minWidth: 0 }}>
            <Typography
            sx={{
              fontFamily: '"Satriya", sans-serif',
              fontSize: '18px !important',
              fontWeight: 400,
              color: '#fff',
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            CRUD EXPERT
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? 0 : layoutWidth,
        flexShrink: 0,
        zIndex: sidebarStackingZIndex,
        transition: 'width 220ms ease',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: 0,
          height: isMobile ? '100dvh' : sidebarPaperHeightForZoomSx(),
          zoom: isMobile ? 1 : APP_PAGE_ZOOM_VAR,
          zIndex: sidebarStackingZIndex,
          background: `linear-gradient(180deg, ${DFT_BRAND.railStart}, ${DFT_BRAND.railEnd})`,
          borderRight: 'none',
          color: DFT_BRAND.navText,
          overflowX: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
          transition: 'width 220ms ease',
        },
      }}
      PaperProps={{
        onDoubleClick: () => {
          if (!isMobile) onToggleOpen?.();
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          px: 0,
          py: 2.25,
        }}
      >
        <Box sx={{ px: expanded ? 1.25 : 1, flexShrink: 0 }}>
          {expanded ? (
            brandMark
          ) : (
            <Tooltip title="CRUD EXPERT" placement="right" arrow>
              {brandMark}
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            /* Touch / wheel scroll without a permanent visible scrollbar. */
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
          }}
        >
          <List dense sx={{ py: 0, px: expanded ? 1.25 : 1 }}>
            {menuItems.map((item) => {
              const showGroup =
                expanded && item.group && item.group !== 'WORKSPACE' && item.group !== lastGroup;
              lastGroup = item.group ?? lastGroup;
              return (
                <React.Fragment key={item.text}>
                  {showGroup && (
                    <Typography
                      sx={{
                        fontSize: { xs: '10px !important', md: '11px !important' },
                        letterSpacing: '1.2px',
                        color: DFT_BRAND.navMuted,
                        mx: 1.25,
                        mt: { xs: 1.75, md: 2, lg: 2.5, xl: 2.75 },
                        mb: { xs: 0.75, md: 1, lg: 1, xl: 1.25 },
                        fontWeight: 600,
                      }}
                    >
                      {item.group}
                    </Typography>
                  )}
                  <ListItem disablePadding sx={{ my: 'clamp(2px, 0.4vh, 6px)' }}>
                    {renderNavButton(item)}
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Box>

        {!isSuperAdmin && !isAdminOnlyRole && (
          <Box
            sx={{
              flexShrink: 0,
              mt: 2,
              borderTop: '1px solid rgba(255,255,255,0.09)',
              pt: 1.75,
              px: expanded ? 1.25 : 1,
            }}
          >
            <List dense sx={{ py: 0 }}>
              <ListItem disablePadding>{renderNavButton(trashItem)}</ListItem>
            </List>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
